import { usd, usd2, pctFrac, mult, pct } from "@/lib/format";
import { Card } from "@/components/common/Ornament";

export function YearTable({ years }) {
  const rows = [
    ["Occupancy", (y) => pct(y.occ * 100)],
    ["ADR", (y) => usd2(y.adr)],
    ["RevPAR", (y) => usd2(y.revpar)],
    ["Room Revenue", (y) => usd(y.roomRev)],
    ["F&B + Other Revenue", (y) => usd(y.otherRev)],
    ["Total Revenue", (y) => usd(y.totalRev), true],
    ["Operating Expenses", (y) => usd(-y.opex)],
    ["Management Fee", (y) => usd(-y.mgmt)],
    ["FF&E Reserve", (y) => usd(-y.reserve)],
    ["Property Taxes + Insurance", (y) => usd(-(y.taxes + y.ins))],
    ["Net Operating Income", (y) => usd(y.noi), true],
    ["Cash Flow Before Debt Service", (y) => usd(y.noi)],
    ["Debt Service", (y) => usd(-y.ds)],
    ["Cash Flow After Debt Service", (y) => usd(y.cfAfter), true],
    ["DSCR", (y) => (y.dscr ? `${y.dscr.toFixed(2)}x` : "—")],
  ];
  return (
    <Card eyebrow="Pro Forma" title="Annual Cash Flow" testId="financial-year-table">
      <div className="overflow-x-auto -mx-2">
        <table className="w-full text-xs sm:text-sm min-w-[640px]">
          <thead>
            <tr className="border-b border-[#0F2E23]">
              <th className="text-left py-2 px-2 font-semibold text-[#3E5C4E]">Line Item</th>
              {years.map((y) => <th key={y.year} className="text-right py-2 px-2 font-mono-num font-semibold text-[#0F2E23]">Y{y.year}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.map(([label, fn, bold]) => (
              <tr key={label} className={`border-b border-[#E2DCD2] ${bold ? "bg-[#F9F6F0] font-semibold text-[#0F2E23]" : "text-slate-700"}`}>
                <td className="py-2 px-2 whitespace-nowrap">{label}</td>
                {years.map((y) => <td key={y.year} className="py-2 px-2 text-right font-mono-num whitespace-nowrap">{fn(y)}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

const color = (v) => {
  if (v === null) return "#f1f5f9";
  if (v < 0) return "rgba(185,28,28,.14)";
  const a = Math.min(0.85, 0.12 + v * 2.5);
  return `rgba(15,46,35,${a})`;
};

export function SensitivityTable({ sens, baseOcc, baseCap }) {
  return (
    <Card eyebrow="Levered IRR" title="Sensitivity: Exit Cap Rate vs. Occupancy" testId="sensitivity-table-irr">
      <div className="overflow-x-auto">
        <table className="w-full text-xs sm:text-sm min-w-[520px] border-separate border-spacing-1">
          <thead>
            <tr>
              <th className="text-left text-[0.65rem] uppercase tracking-wider text-slate-500 p-2">Occ. ↓ / Exit Cap →</th>
              {sens.caps.map((c) => <th key={c} className={`p-2 font-mono-num ${c === baseCap ? "text-[#9A7A1E]" : "text-[#0F2E23]"}`}>{pct(c)}</th>)}
            </tr>
          </thead>
          <tbody>
            {sens.occs.map((o, r) => (
              <tr key={o}>
                <td className={`p-2 font-mono-num font-semibold ${o === baseOcc ? "text-[#9A7A1E]" : "text-[#0F2E23]"}`}>{pct(o)}</td>
                {sens.grid[r].map((v, k) => {
                  const base = o === baseOcc && sens.caps[k] === baseCap;
                  const dark = v !== null && v > 0.12;
                  return (
                    <td key={k} data-testid={`sensitivity-cell-${r}-${k}`} className={`p-2 text-center font-mono-num rounded ${base ? "ring-2 ring-[#C59B27]" : ""}`} style={{ background: color(v), color: dark ? "#F9F6F0" : "#1A1D1A" }}>
                      {pctFrac(v)}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-slate-500 mt-3">Gold outline = current scenario. All other inputs held constant.</p>
    </Card>
  );
}

export function metricRows() {
  return [
    ["Total Project Cost", (r) => usd(r.totalCost)],
    ["Cost per Key", (r) => usd(r.costPerKey)],
    ["Equity Required", (r) => usd(r.equity)],
    ["Year 1 RevPAR", (r) => usd2(r.revpar1)],
    ["Year 1 NOI", (r) => usd(r.noi1)],
    ["Year 1 DSCR", (r) => (r.dscr1 ? `${r.dscr1.toFixed(2)}x` : "—")],
    ["Year 1 Cash-on-Cash", (r) => pctFrac(r.coc1)],
    ["Yield on Cost (stabilized)", (r) => pctFrac(r.yieldOnCost)],
    ["Unlevered IRR", (r) => pctFrac(r.unleveredIRR)],
    ["Levered IRR", (r) => pctFrac(r.leveredIRR)],
    ["Equity Multiple", (r) => mult(r.equityMultiple)],
    ["Exit Value", (r) => usd(r.exitValue)],
    ["Exit Value per Key", (r) => usd(r.exitPerKey)],
  ];
}
